import React from 'react';
import classnames from 'classnames';
import { PatientSidebarTab } from '../../../../models/enums/PatientSidebarTabs';
import mobxInjectSelect from '../../../../common/utils/mobxInjectSelect';
import { type Entity } from '../../../../types';
import styles from './SearchParityResults.module.css';
import { KEYMAP } from 'common/constants';
import { useAppSelector, useAppDispatch } from 'redux-stores';
import { trackSearchLoadEvent, EVENTS } from 'redux-stores/KeywordSearch/thunk';

type Filter = 'Inbox' | 'Teams' | 'Groups' | 'Forums';

type MobxProps = {
  selectedFilter: Filter;
  setFilter: (filter: Filter) => void;
  setSidebarTab: (tab: PatientSidebarTab) => void;
  promptToJoinForum: (entity: Entity) => void;
  showPatientFilteredConversations: (entity: Entity) => void;
  composeToSearchResult: (entity: Entity) => void;
  setEntityInboxSearchGainsight: (entity: Entity) => void;
  logInboxSearchGainsight: () => void;
};

type ItemProps = {
  children?: React.ReactNode;
  entity: Entity;
  lastItem?: boolean;
  itemKey?: number | string;
  originalEntityType?: string;
};

const Item = ({
  children,
  entity,
  lastItem,
  itemKey,
  originalEntityType,
  selectedFilter,
  setFilter,
  promptToJoinForum,
  setSidebarTab,
  showPatientFilteredConversations,
  composeToSearchResult,
  setEntityInboxSearchGainsight,
  logInboxSearchGainsight,
}: ItemProps & MobxProps) => {
  const { isKeywordSearchModeOn } = useAppSelector((state) => state.keywordSearch);
  const dispatch = useAppDispatch();

  const _setFilter = (entity: Entity): Filter => {
    const { $entityType: entityType, groupType } = entity;
    if (selectedFilter === 'Forums' && entityType === 'group' && groupType === 'FORUM') {
      return 'Forums';
    } else if (selectedFilter === 'Groups' && entityType === 'group' && groupType === 'GROUP') {
      return 'Groups';
    } else if (selectedFilter === 'Teams' && entityType === 'team') {
      return 'Teams';
    }
    return 'Inbox';
  };

  const onClick = async (entity: Entity) => {
    const {
      $entityType: entityType,
      groupType,
      hasCurrentUser,
      isPatient,
      isPatientContact,
    } = entity;

    if (isKeywordSearchModeOn) {
      dispatch(trackSearchLoadEvent(EVENTS.OPEN_CONV_FOR_ENTITY));
    }

    const filter = _setFilter(entity);
    setFilter(filter);
    if (entityType === 'group' && groupType === 'FORUM' && !hasCurrentUser) {
      promptToJoinForum(entity);
    } else if (isPatient || isPatientContact) {
      setSidebarTab('messages');
      showPatientFilteredConversations(entity);
    } else {
      composeToSearchResult(entity);
    }

    const entityForTracking = originalEntityType
      ? ({
          ...entity,
          $entityType: originalEntityType,
        } as Entity)
      : entity;

    setEntityInboxSearchGainsight(entityForTracking);

    if (isKeywordSearchModeOn) {
      logInboxSearchGainsight();
    }
  };

  const setDivider = () => {
    if (lastItem === true) return null;
    if ('patient' in entity && entity.patient?.contacts && entity.patient?.contacts.length > 0) {
      return styles.partialBorder;
    }
    if (
      ('patient' in entity && entity.patient) ||
      ('patientContact' in entity && entity.patientContact)
    ) {
      return styles.fullBorder;
    } else {
      return styles.partialBorder;
    }
  };

  return (
    <div
      key={itemKey}
      className={classnames(styles.item, setDivider())}
      onClick={() => onClick(entity)}
      onKeyDown={(e) => {
        if (e.key === KEYMAP.ENTER || e.key === KEYMAP.SPACE) {
          onClick(entity);
        }
      }}
      role="button"
    >
      {children}
    </div>
  );
};

export default mobxInjectSelect<ItemProps, MobxProps>({
  composeMessageStore: ['composeToSearchResult'],
  conversationStore: ['showPatientFilteredConversations'],
  groupStore: ['promptToJoinForum'],
  patientStore: ['setSidebarTab'],
  rosterStore: ['setFilter', 'selectedFilter'],
  trackerStore: ['logInboxSearchGainsight', 'setEntityInboxSearchGainsight'],
})(Item);
